import type { MetadataTier, MetadataViolation } from './metadataCriteria';

export type HumanVerifySeverity = 'block' | 'warn' | 'info';
export type HumanVerifyMachineStatus = 'flagged' | 'clean' | 'n/a';
export type HumanVerifyItemStatus = 'needs_review' | 'verified' | 'dismissed';

export interface HumanVerifyItem {
  id: string;
  category: string;
  severity: HumanVerifySeverity;
  prompt: string;
  machine_status: HumanVerifyMachineStatus;
  machine_context?: string;
  /** Structured evidence for the "See details" disclosure (never rendered
   *  verbatim as JSON in the UI — the human sentence lives in machine_context). */
  evidence?: Record<string, unknown>;
  status: HumanVerifyItemStatus;
  note?: string;
  verified_by?: string;
  verified_at?: string;
}

export interface HumanVerifyState {
  seeded_at?: string;
  code_sha?: string;
  items: HumanVerifyItem[];
}

export interface HumanVerifyCarrier {
  items?: unknown;
  human_verify?: unknown;
  generation_metadata?: {
    human_verify?: unknown;
  } & Record<string, unknown>;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

function normalizeSeverity(value: unknown): HumanVerifySeverity {
  return value === 'block' || value === 'info' ? value : 'warn';
}

function normalizeMachineStatus(value: unknown): HumanVerifyMachineStatus {
  return value === 'flagged' || value === 'clean' || value === 'n/a' ? value : 'n/a';
}

function normalizeItemStatus(value: unknown): HumanVerifyItemStatus {
  if (value === 'verified' || value === 'dismissed') return value;
  return 'needs_review';
}

const LEGACY_CONTEXT_SENTENCE = 'Flagged for review — open the details for what the check found.';

/**
 * Rows seeded before the human-sentence redesign stored the raw JSON dump in
 * machine_context. The backend heals these on read, but guard here too — the
 * dialog must never print a JSON blob at a reviewer, whatever the source.
 */
function legacyContextFields(
  machineContext: string | undefined,
  evidence: Record<string, unknown> | undefined,
): Pick<HumanVerifyItem, 'machine_context' | 'evidence'> {
  if (!machineContext || !/^\s*[[{]/.test(machineContext)) {
    return { machine_context: machineContext, evidence };
  }
  let parsed: Record<string, unknown> = { raw: machineContext };
  try {
    const candidate: unknown = JSON.parse(machineContext);
    if (isObject(candidate)) parsed = candidate;
  } catch {
    // Malformed dump: keep the payload as-is behind the details table.
  }
  return { machine_context: LEGACY_CONTEXT_SENTENCE, evidence: evidence ?? parsed };
}

function normalizeItem(value: unknown): HumanVerifyItem | null {
  if (!isObject(value)) return null;
  const id = nonEmptyString(value.id);
  const prompt = nonEmptyString(value.prompt);
  if (!id || !prompt) return null;
  return {
    id,
    category: nonEmptyString(value.category) ?? 'general',
    severity: normalizeSeverity(value.severity),
    prompt,
    machine_status: normalizeMachineStatus(value.machine_status),
    ...legacyContextFields(
      nonEmptyString(value.machine_context),
      isObject(value.evidence) ? (value.evidence as Record<string, unknown>) : undefined,
    ),
    status: normalizeItemStatus(value.status),
    note: nonEmptyString(value.note),
    verified_by: nonEmptyString(value.verified_by),
    verified_at: nonEmptyString(value.verified_at),
  };
}

export function getHumanVerifyState(content: HumanVerifyCarrier | null | undefined): HumanVerifyState | null {
  const raw = isObject(content) && Array.isArray(content.items)
    ? content
    : content?.human_verify ?? content?.generation_metadata?.human_verify;
  if (!isObject(raw)) return null;
  const items = Array.isArray(raw.items)
    ? raw.items.map(normalizeItem).filter((item): item is HumanVerifyItem => item !== null)
    : [];
  return {
    seeded_at: nonEmptyString(raw.seeded_at),
    code_sha: nonEmptyString(raw.code_sha),
    items,
  };
}

export function unresolvedHumanVerifyItems(content: HumanVerifyCarrier | null | undefined): HumanVerifyItem[] {
  return getHumanVerifyState(content)?.items.filter((item) => item.status === 'needs_review') ?? [];
}

export function countUnresolvedHumanVerify(content: HumanVerifyCarrier | null | undefined): number {
  return unresolvedHumanVerifyItems(content).length;
}

export function hasUnresolvedHumanVerify(content: HumanVerifyCarrier | null | undefined): boolean {
  return countUnresolvedHumanVerify(content) > 0;
}

export function humanVerifySeverityLabel(severity: HumanVerifySeverity): string {
  if (severity === 'block') return 'Sign-off';
  if (severity === 'warn') return 'Warning';
  return 'Info';
}

export function humanVerifyMachineLabel(status: HumanVerifyMachineStatus): string {
  if (status === 'flagged') return 'Auto flagged';
  if (status === 'clean') return 'Auto clean';
  return 'Auto n/a';
}

export function humanVerifyStatusLabel(status: HumanVerifyItemStatus): string {
  if (status === 'needs_review') return 'Needs review';
  if (status === 'verified') return 'Verified';
  return 'Dismissed';
}

export function humanVerifyCategoryLabel(category: string): string {
  return category
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function humanVerifyItemToViolation(item: HumanVerifyItem): MetadataViolation {
  const tier: MetadataTier = item.severity;
  const context = item.machine_context ? ` Context: ${item.machine_context}` : '';
  return {
    ruleId: `human_verify_${item.id}`,
    field: 'human_verify',
    tier,
    severity: tier,
    message: `${item.prompt}${context}`,
    sourceNote: `generation_metadata.human_verify:${item.category}`,
  };
}

export function humanVerifyViolations(content: HumanVerifyCarrier | null | undefined): MetadataViolation[] {
  return unresolvedHumanVerifyItems(content).map(humanVerifyItemToViolation);
}

export function getUnverifiedHumanVerifyItems(content: HumanVerifyCarrier | null | undefined): HumanVerifyItem[] {
  return unresolvedHumanVerifyItems(content);
}

export function getHumanVerifyUnverifiedCount(content: HumanVerifyCarrier | null | undefined): number {
  return countUnresolvedHumanVerify(content);
}

export function hasUnverifiedHumanVerify(content: HumanVerifyCarrier | null | undefined): boolean {
  return hasUnresolvedHumanVerify(content);
}

export function formatHumanVerifyTimestamp(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

// Canonical Tailwind token classes for human-verify chips. Single source of
// truth so the editor rail row and the QA-workbench mirror never drift
// (R2 review: dismissed/n-a chip colors had diverged between the two).
export function humanVerifySeverityClasses(severity: HumanVerifySeverity): string {
  if (severity === 'block') return 'border-coral bg-coral text-primary-foreground';
  if (severity === 'warn') return 'border-coral/30 bg-coral-light text-coral-dark';
  return 'border-teal/30 bg-teal-light text-teal-dark';
}

export function humanVerifyMachineClasses(status: HumanVerifyMachineStatus): string {
  if (status === 'flagged') return 'border-coral/30 bg-coral-light text-coral-dark';
  if (status === 'clean') return 'border-teal/30 bg-teal-light text-teal-dark';
  return 'border-border bg-warm-bg text-muted-foreground';
}

export function humanVerifyStatusClasses(status: HumanVerifyItemStatus): string {
  if (status === 'verified') return 'border-teal/30 bg-teal-light text-teal-dark';
  if (status === 'dismissed') return 'border-border bg-warm-bg text-muted-foreground';
  return 'border-coral/30 bg-coral-light text-coral-dark';
}
